import { Helmet } from "react-helmet-async";
import { organizationSchema } from "@/lib/seoConfig";
import { getPublicPages } from "@/lib/siteRoutes";

interface SEOProps {
  title: string; description: string; canonical?: string;
  ogTitle?: string; ogDescription?: string; ogImage?: string; ogType?: string;
  twitterCard?: string; keywords?: string; structuredData?: object;
  noindex?: boolean; publishedTime?: string; modifiedTime?: string;
}
const SITE_URL = "https://oc-international.com";
const SITE_NAME = "OC Recruitment";
const DEFAULT_OG_IMAGE = "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/hero-packaging-products-jTcsMjjFhH8thZMMjwdh3M.webp";

export default function SEO({ title, description, canonical, ogTitle, ogDescription, ogImage, ogType = "website", twitterCard = "summary_large_image", keywords, structuredData, noindex = false, publishedTime, modifiedTime }: SEOProps) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : undefined;
  const imageUrl = new URL(ogImage || DEFAULT_OG_IMAGE, SITE_URL).href;
  const graph: Record<string, unknown>[] = noindex ? [] : [organizationSchema];
  if (structuredData) {
    const supplied = structuredData as Record<string, unknown>;
    const nodes = Array.isArray(structuredData) ? structuredData : Array.isArray(supplied["@graph"]) ? supplied["@graph"] : [supplied];
    for (const node of nodes) {
      const item = node as Record<string, unknown>;
      if (!item["@id"] || !graph.some(existing => existing["@id"] === item["@id"])) graph.push(item);
    }
  }
  if (canonicalUrl && !noindex) {
    graph.push({
      "@type": "WebPage", "@id": `${canonicalUrl}#webpage`, url: canonicalUrl,
      name: fullTitle, description, inLanguage: "en-US", isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
    });
    if (canonical !== "/") {
      const pages = getPublicPages();
      const chain = [{ path: "/", label: "Home" }];
      if (canonical?.startsWith("/sectors/")) chain.push({ path: "/packaging-sectors", label: "Packaging Sectors" });
      if (canonical?.startsWith("/insights/")) chain.push({ path: "/insights", label: "Insights" });
      chain.push({ path: canonical!, label: pages.find(page => page.path === canonical)?.label ?? fullTitle });
      graph.push({
        "@type": "BreadcrumbList", "@id": `${canonicalUrl}#breadcrumbs`,
        itemListElement: chain.map((item, i) => ({ "@type": "ListItem", position: i + 1, name: item.label, item: `${SITE_URL}${item.path}` })),
      });
    }
  }
  return <Helmet>
    <title>{fullTitle}</title>
    <meta name="description" content={description} />
    <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large"} />
    {keywords && <meta name="keywords" content={keywords} />}
    {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
    <meta property="og:title" content={ogTitle || fullTitle} />
    <meta property="og:description" content={ogDescription || description} />
    <meta property="og:type" content={ogType} />
    <meta property="og:image" content={imageUrl} />
    <meta property="og:image:alt" content="OC Recruitment — specialist US packaging sales recruitment" />
    <meta property="og:site_name" content={SITE_NAME} />
    <meta property="og:locale" content="en_US" />
    {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
    {publishedTime && <meta property="article:published_time" content={publishedTime} />}
    {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
    <meta name="twitter:card" content={twitterCard} />
    <meta name="twitter:title" content={ogTitle || fullTitle} />
    <meta name="twitter:description" content={ogDescription || description} />
    <meta name="twitter:image" content={imageUrl} />
    <meta name="twitter:image:alt" content="OC Recruitment — specialist US packaging sales recruitment" />
    {graph.length > 0 && <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@graph": graph }).replace(/</g, "\\u003c")}</script>}
  </Helmet>;
}
